import { webErrorCodesDispatcher } from '@app/helpers/apiCall';
import {
  EPrinterStatus,
  IPrinterUpdateWSR,
  IUpdateSettingsResponse,
  actionCreatorsApp,
  actionCreatorsLocationSettings,
  actionCreatorsPrinterCenter,
  websocketMapType,
} from '@westondev/tableturn-core';
import i18n from '../../i18n.config';
import { getRequiredActionsData } from '../menu/menuWebActions';
import {
  printersSelector,
  selectLocationShowPrinterStatusNotifications,
  selectRoles,
} from '../selectors/settingsSelectors';
import { AppThunk } from '../store';
import {
  setLoggedInUserAndRole,
  setShowToast,
  webSocketRouter,
} from './actions';
import { IDashboard } from '../home/types';
import { setCurrentDashboard, setCurrentEditDashboard } from '../home/actions';
import { isEqual } from 'lodash';
import { BroadcastChannel } from 'broadcast-channel';
import { selectLoggedInRoleId } from '../users/usersSelectors';

export const webSocketRouterWeb =
  (data: websocketMapType): AppThunk<void> =>
  dispatch => {
    const currentDeviceLicenseSettingsId = '';

    return dispatch(webSocketRouter(data, currentDeviceLicenseSettingsId));
  };

export const handlePrinterStatusWsWeb = (
  payload: IPrinterUpdateWSR,
): AppThunk<void> => {
  return async (dispatch, getState) => {
    const state = getState();
    const showPrinterStatusNotifications =
      selectLocationShowPrinterStatusNotifications(state);

    const printers = printersSelector(state);

    if (
      payload.status !== EPrinterStatus.ACTIVE &&
      showPrinterStatusNotifications
    ) {
      dispatch(
        setShowToast({
          type: 'error',
          title: i18n.t(
            'navBar.common.printerCenter.printer.printerErrorTitle',
          ),
          description: `${printers[payload.printerId]?.name} - ${i18n.t(
            `printerStatus.${payload.status}`,
          )}`,
        }),
      );
    }

    if (payload.status === EPrinterStatus.ACTIVE) {
      try {
        const printerJobsIds = payload.printJobs.map(printJob => printJob.id);

        dispatch(
          actionCreatorsPrinterCenter.clearPrinterErrorsForPrintJobs(
            printerJobsIds,
          ) as unknown as AppThunk<void>,
        );
      } catch (err) {
        console.error(err);
      }
    }

    dispatch(actionCreatorsLocationSettings.setPrinterStatusWs(payload));
  };
};

export const setMasterLocationWeb =
  (onContinue?: () => void): AppThunk<void> =>
  async (dispatch, getState) => {
    const state = getState();
    const { organizationId } = getRequiredActionsData(state);
    return dispatch(
      actionCreatorsApp.setMasterLocation(
        organizationId,
        0,
        webErrorCodesDispatcher,
        undefined,
        onContinue,
      ),
    );
  };

export const handleDashboardsWsWeb = (
  payload: IUpdateSettingsResponse,
): AppThunk<void> => {
  return async (dispatch, getState) => {
    const state = getState();

    const settingsChanges = payload;
    const currentDashboard = state.home.currentDashboard;
    const currentEditDashboard = state.home.currentEditDashboard;
    const dashboardChannel = new BroadcastChannel('dashboards');

    if (currentDashboard) {
      const dashboardSettings = Object.values(
        settingsChanges.location.dashboards,
      ).find(
        dashboard => dashboard.id === currentDashboard.id,
      ) as IDashboard | null;

      if (dashboardSettings) {
        const updatedDashboard = {
          ...currentDashboard,
          id: dashboardSettings.id,
          name: dashboardSettings.name,
          layout: dashboardSettings.layout,
        };
        if (!isEqual(currentDashboard, updatedDashboard)) {
          dispatch(
            setCurrentDashboard({
              ...currentDashboard,
              name: dashboardSettings.name,
              layout: dashboardSettings.layout,
            }),
          );

          dashboardChannel.postMessage({
            action: 'newChanges',
          });
        }
      } else {
        dispatch(setCurrentDashboard(null));
        if (currentEditDashboard) {
          dashboardChannel.postMessage({
            action: 'cancelChanges',
          });
          dispatch(
            setShowToast({
              type: 'warning',
              title: 'Dashboard Removed',
              description: 'The dashboard you were editing has been removed.',
            }),
          );
        }
      }
    } else if (currentEditDashboard) {
      const dashboardId = currentEditDashboard.selectedDashboard.id;
      const dashboardSettings = Object.values(
        settingsChanges.location.dashboards,
      ).find(dashboard => dashboard.id === dashboardId) as IDashboard | null;

      if (!dashboardSettings) {
        dispatch(setCurrentEditDashboard(null));
      }
    } else if (Object.values(settingsChanges.location.dashboards).length > 0) {
      const loggedInRoleId = selectLoggedInRoleId(state);
      const roles = selectRoles(state);
      const locationRole = roles[loggedInRoleId];

      if (locationRole.defaultDashboardId) {
        dispatch(setLoggedInUserAndRole(loggedInRoleId));
        dashboardChannel.postMessage({
          action: 'newChanges',
        });
      }
    }
  };
};
